/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useAppDispatch,
  useGetBusinessData,
  useSelectedCity,
  useVendorTypes,
  useUserData,
} from "../../../Utility";
import { onsaveSelectedCity } from "../../../Store/Slices/utilitySlice";
import { brand, cities, header } from "../../../config/appData";
import "./Header.css";
import { onGetBusinesses } from "../../../Store/Slices/businessSlice";
import {
  capsFirstLetter,
  isBusinessUser,
} from "../../../utils/UtilityFunctions";
import { getAdminAuth } from "../../../utils/auth";
import {
  buildHomeCityPath,
  buildBlogPath,
  buildVenuesListingPath,
  buildVendorsListingPath,
  getListingCityFromPathname,
} from "../../../utils/listingPaths";
function Header({
  basic = false,
  onProfileClick,
  onEnquireClick,
  onListBusinessClick,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const isLoggedIn = useSelector((state) => state.utilityReducer?.isLoggedIn);
  const isAdminLoggedIn = Boolean(getAdminAuth());
  const isHomePage =
    location.pathname === "/" || location.pathname.startsWith("/city/");
  const isBlogRoute =
    location.pathname === "/blog" || location.pathname.startsWith("/blog/");
  const businessData = useGetBusinessData();
  const businessesList = businessData?.businessesList ?? [];
  const isBusiness = isBusinessUser();
  const urlCity =
    getListingCityFromPathname(location.pathname) ||
    searchParams.get("city") ||
    "";
  const storedCity = useSelectedCity() || "";
  const selectedCity = urlCity;
  // Dropdown value from URL so when no city in URL both homepage dropdowns show "Select city"
  const displayCity = urlCity;
  const venueTypes = [
    ...new Set(
      businessesList
        .filter((item) => item.venue?.venue_type && item.city.toLowerCase() === selectedCity.toLowerCase()) 
        .map((item) => {
          const type = capsFirstLetter(item.venue.venue_type);

          if (selectedCity) {
            return `${type}s in ${selectedCity}`;
          } else {
            return `${type}s`;
          }
        }),
    ),

    selectedCity
      ? `Explore All Venues in ${selectedCity}`
      : "Explore All Venues",
  ];
  const vendorTypes = useVendorTypes(businessesList,true,selectedCity);
  const venueTypesTabular = venueTypes.length > 4;
  const vendorTypesTabular = true; /* vendors dropdown always single column */
  const menuLoading = businessData?.getBusinessesLoading;

  const topRankedVenuesCount = (businessesList || [])?.filter(
    (b) =>
      b?.business_type === "venue" &&
      b?.top_rank != null && b?.city?.toLowerCase() === selectedCity?.toLowerCase() &&
      !Number?.isNaN(Number(b?.top_rank)) 
  ).length;

  const topRankedVendorsCount = (businessesList || [])?.filter(
    (b) =>
      b?.business_type === "vendor" &&
      b?.city.toLowerCase() === selectedCity.toLowerCase() &&
      b?.top_rank != null &&
      b?.top_rank !== "" &&
      !Number?.isNaN(Number(b?.top_rank)) 
    
  )?.length;

  const [dropdownState, setDropdownState] = useState({
    vendors: { isHovered: false, position: { top: 0, left: 0 } },
    venues: { isHovered: false, position: { top: 0, left: 0 } },
  });

  const [mobileState, setMobileState] = useState({
    menuOpen: false,
    venuesOpen: false,
    vendorsOpen: false,
  });

  const [scrollState, setScrollState] = useState({
    isScrolledPastCities: false,
  });

  const isHeaderCollapsed = !isHomePage || scrollState.isScrolledPastCities;

  const userData = useUserData();
  const userName = userData?.name || "";
  const getInitials = (name) => {
    if (!name || !name.trim()) return null;
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      const w = words[0];
      return w.length >= 2 ? (w[0] + w[w.length - 1]).toUpperCase() : w[0].toUpperCase();
    }
    const first = words[0].charAt(0).toUpperCase();
    const last = words[words.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  };
  const userInitials = getInitials(userName);

  // Consolidated refs
  const vendorsMenuRef = useRef(null);
  const venuesMenuRef = useRef(null);
  const vendorsLinkRef = useRef(null);
  const venuesLinkRef = useRef(null);
  const citySelectWrapperRef = useRef(null);
  const vendorsCloseTimeoutRef = useRef(null);
  const venuesCloseTimeoutRef = useRef(null);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileState.menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileState.menuOpen]);

  // Memoized position updaters
  const updatePosition = useCallback((type) => {
    const linkRef = type === "vendors" ? vendorsLinkRef : venuesLinkRef;
    if (linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      setDropdownState((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          position: { top: rect.bottom + 8, left: rect.left },
        },
      }));
    }
  }, []);

  // Dropdown hover effects
  useEffect(() => {
    if (dropdownState.vendors.isHovered) {
      updatePosition("vendors");
      const handleUpdate = () => updatePosition("vendors");
      window.addEventListener("scroll", handleUpdate);
      window.addEventListener("resize", handleUpdate);
      return () => {
        window.removeEventListener("scroll", handleUpdate);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [dropdownState.vendors.isHovered, updatePosition]);

  useEffect(() => {
    if (dropdownState.venues.isHovered) {
      updatePosition("venues");
      const handleUpdate = () => updatePosition("venues");
      window.addEventListener("scroll", handleUpdate);
      window.addEventListener("resize", handleUpdate);
      return () => {
        window.removeEventListener("scroll", handleUpdate);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [dropdownState.venues.isHovered, updatePosition]);

  // Scroll detection: one-line header after user scrolls (homepage + all pages)
  const SCROLL_THRESHOLD = 80;

  useEffect(() => {
    if (!isHomePage) {
      setScrollState({ isScrolledPastCities: true });
      return;
    }

    const checkScrollPosition = () => {
      const scrollY = window.scrollY ?? document.documentElement.scrollTop;
      setScrollState({ isScrolledPastCities: scrollY > SCROLL_THRESHOLD });
    };

    checkScrollPosition();
    const timeoutId = setTimeout(checkScrollPosition, 100);

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", checkScrollPosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [isHomePage]);

  // Memoized handlers
  const handleCityChange = useCallback(
    (e) => {
      const city = e.target.value;
      const onHomePath =
        location.pathname === "/" ||
        location.pathname.startsWith("/city/");
      if (onHomePath) {
        if (city) {
          navigate(buildHomeCityPath(city), { replace: true });
        } else {
          navigate("/", { replace: true });
        }
        dispatch(onsaveSelectedCity({ selectedCity: city || "" }));
        return;
      }
      if (city) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("city", city);
        setSearchParams(newParams);
        dispatch(onsaveSelectedCity({ selectedCity: city }));
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("city");
        setSearchParams(newParams);
        dispatch(onsaveSelectedCity({ selectedCity: "" }));
      }
    },
    [
      dispatch,
      location.pathname,
      navigate,
      searchParams,
      setSearchParams,
    ],
  );

  // Keep Redux city in sync with URL when URL changes (e.g. deep links)
  useEffect(() => {
    if (urlCity && urlCity !== storedCity) {
      dispatch(onsaveSelectedCity({ selectedCity: urlCity }));
    } else if (!urlCity && storedCity) {
      dispatch(onsaveSelectedCity({ selectedCity: "" }));
    }
  }, [dispatch, storedCity, urlCity]);

  const toggleMobileMenu = useCallback(() => {
    setMobileState((prev) => ({
      ...prev,
      menuOpen: !prev.menuOpen,
      venuesOpen: prev.menuOpen ? false : prev.venuesOpen,
      vendorsOpen: prev.menuOpen ? false : prev.vendorsOpen,
    }));
  }, []);

  const handleDropdownHover = useCallback(
    (type, isHovered) => {
      const closeTimeoutRef = type === "vendors" ? vendorsCloseTimeoutRef : venuesCloseTimeoutRef;
      if (isHovered) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setDropdownState((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            isHovered: true,
            position: prev[type].position,
          },
        }));
        updatePosition(type);
      } else {
        closeTimeoutRef.current = setTimeout(() => {
          closeTimeoutRef.current = null;
          setDropdownState((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              isHovered: false,
            },
          }));
        }, 200);
      }
    },
    [updatePosition],
  );

  const closeMobileMenu = () =>
    setMobileState({
      menuOpen: false,
      venuesOpen: false,
      vendorsOpen: false,
    });

  const navigateVenueFromLabel = (venueLabel) => {
    if (
      venueLabel === `Explore All Venues in ${selectedCity}` ||
      venueLabel === "Explore All Venues"
    ) {
      navigate(
        buildVenuesListingPath("all", {
          ...(selectedCity ? { city: selectedCity } : {}),
        }),
      );
      return;
    }

    const type = venueLabel
      .toLowerCase()
      .replace(selectedCity ? ` in ${selectedCity.toLowerCase()}` : "", "")
      .replace(/s$/, "");

    navigate(
      buildVenuesListingPath(type, {
        ...(selectedCity ? { city: selectedCity } : {}),
      }),
    );
  };

  const navigateVendorFromLabel = (vendorLabel) => {
    if (
      vendorLabel === `Explore All Vendors in ${selectedCity}` ||
      vendorLabel === "Explore All Vendors"
    ) {
      navigate(
        buildVendorsListingPath({
          category: "all",
          ...(selectedCity ? { city: selectedCity } : {}),
        }),
      );
      return;
    }

    const type = vendorLabel
      .toLowerCase()
      .replace(selectedCity ? ` in ${selectedCity.toLowerCase()}` : "")
      .replace(/s$/, "");

    navigate(
      buildVendorsListingPath({
        category: type,
        ...(selectedCity ? { city: selectedCity } : {}),
      }),
    );
  };

  useEffect(() => {
    dispatch(
      onGetBusinesses({
        limit: 50,
        offset: 0,
        ...(urlCity ? { city: urlCity } : {}),
      }),
    );
  }, [dispatch, urlCity]);

  const showWhiteHeaderLogo =
    isHomePage &&
    !scrollState.isScrolledPastCities &&
    !mobileState.menuOpen;

  const LogoTag = isBlogRoute ? "div" : "h1";

  return (
    <header
      className={`header ${basic ? "header-basic" : ""} ${!isHomePage || scrollState.isScrolledPastCities ? "header-solid" : ""} ${!isHomePage ? "header-compact" : ""}`}
    >
      <div className="header-top">
        <div className="header-left">
          {!basic && (
            <button
              className="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={toggleMobileMenu}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {mobileState.menuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          )}
          <LogoTag
            className={`logo ${basic ? "logo-basic" : ""} ${isHomePage && !scrollState.isScrolledPastCities ? "logo-home" : "logo-inner"}`}
            onClick={
              basic
                ? undefined
                : () => {
                    navigate(
                      selectedCity
                        ? buildHomeCityPath(selectedCity)
                        : "/",
                    );
                  }
            }
            style={basic ? {} : { cursor: "pointer" }}
          >
            <img
              src={showWhiteHeaderLogo ? "/logo-white.png" : "/black.png"}
              alt={brand.name || "WeddingzVenue"}
              className={`header-logo-img ${showWhiteHeaderLogo ? "header-logo-img-home" : "header-logo-img-inner"}`}
              decoding="async"
            />
            <span className="logo-icon logo-icon-fallback" aria-hidden="true">{brand.logoIcon || header.logoIcon || "WV"}</span>
            <span className="logo-text logo-text-fallback">{brand.name || header.logoText}</span>
          </LogoTag>
          {!basic && (
            <div className="city-select-wrapper" ref={citySelectWrapperRef}>
              <svg
                className="location-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <select
                className="city-select"
                value={displayCity || ""}
                onChange={handleCityChange}
              >
                <option value="">
                  {header.citySearchPlaceholder}
                </option>
                {cities.map((city, index) => (
                  <option key={index} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="header-right">
          {!basic && !isLoggedIn && !isAdminLoggedIn && !isHeaderCollapsed && (
            <button
              className="header-link desktop-only"
              onClick={onListBusinessClick}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              {header.listBusinessText}
            </button>
          )}
          {basic ? (
            <span className="icon-button icon-button-basic" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </span>
          ) : isAdminLoggedIn ? (
            <button
              className="header-admin-dashboard-btn"
              aria-label="Admin Dashboard"
              onClick={() => navigate("/admin/dashboard")}
            >
              <span className="header-admin-dashboard-btn__full">Admin Dashboard</span>
              <span className="header-admin-dashboard-btn__short">Admin</span>
            </button>
          ) : isLoggedIn ? (
            <button
              className="icon-button header-profile-initials-btn"
              aria-label="Profile"
              onClick={onProfileClick}
            >
              {userInitials ? (
                <span className="header-profile-initials" aria-hidden="true">
                  {userInitials}
                </span>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              )}
            </button>
          ) : (
            !mobileState.menuOpen && (
              <button
                className="header-login-btn"
                aria-label="Login"
                onClick={onProfileClick}
              >
                Login
              </button>
            )
          )}
          {!isBusiness && !isAdminLoggedIn && (!isHeaderCollapsed || isLoggedIn) && (
            basic ? (
              <span
                className="icon-button call-button desktop-only icon-button-basic"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <span>{header.callButtonText}</span>
              </span>
            ) : (
              <button
                className="icon-button call-button desktop-only"
                aria-label="Enquire"
                onClick={onEnquireClick}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <span>{header.callButtonText}</span>
              </button>
            )
          )}
        </div>
      </div>

      <nav
        className={`navigation ${!basic && mobileState.menuOpen ? "mobile-menu-open" : ""} ${menuLoading && !basic ? "header-skeleton-nav" : ""}`}
      >
        {menuLoading && !basic ? (
          <>
            <div className="header-skeleton-nav-item skeleton-pulse" />
            <div className="header-skeleton-nav-item skeleton-pulse" />
            <div className="header-skeleton-nav-item skeleton-pulse" />
            <div className="header-skeleton-nav-item skeleton-pulse" />
            <div className="header-skeleton-nav-item skeleton-pulse" />
            <div className="header-skeleton-nav-item skeleton-pulse desktop-only" />
            <div className="header-skeleton-nav-item skeleton-pulse desktop-only" />
          </>
        ) : basic ? (
          <>
            <span className="nav-link desktop-only">Venues</span>
            <span className="nav-link desktop-only">Vendors</span>
            {header.navItems
              .filter(
                (item) => item.label !== "Venues" && item.label !== "Vendors",
              )
              .map((item, index) => (
                <span key={index} className="nav-link desktop-only">
                  {item.label}
                </span>
              ))}
          </>
        ) : (
          <>
            <div
              className="nav-link-wrapper desktop-only"
              ref={venuesMenuRef}
              onMouseEnter={() => handleDropdownHover("venues", true)}
              onMouseLeave={() => handleDropdownHover("venues", false)}
            >
              <div ref={venuesLinkRef} className="nav-link">
                Venues
              </div>
              {dropdownState.venues.isHovered &&
                createPortal(
                  <div
                    className={`nav-dropdown-menu venues-dropdown nav-dropdown-menu-portal ${venueTypesTabular ? "nav-dropdown-menu--single-column" : ""}`}
                    style={{
                      position: "fixed",
                      top: `${dropdownState.venues.position.top}px`,
                      left: `${dropdownState.venues.position.left}px`,
                    }}
                    onMouseEnter={() => handleDropdownHover("venues", true)}
                    onMouseLeave={() => handleDropdownHover("venues", false)}
                  >
                    {venueTypesTabular ? (
                      <table className="nav-dropdown-table" role="presentation">
                        <tbody>
                          {topRankedVenuesCount > 0 && (
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="nav-dropdown-item nav-dropdown-item-top-ranked"
                                  onClick={() => {
                                    navigate(
                                      buildVenuesListingPath("all", {
                                        hasTopRank: true,
                                        ...(selectedCity ? { city: selectedCity } : {}),
                                      }),
                                    );
                                    setDropdownState((prev) => ({
                                      ...prev,
                                      venues: { ...prev.venues, isHovered: false },
                                    }));
                                  }}
                                >
                                  <span className="nav-dropdown-item-top-ranked-text">Top Venues</span>
                                  <span className="nav-dropdown-item-top-ranked-star" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                  </span>
                                </button>
                              </td>
                            </tr>
                          )}
                          {venueTypes.map((venue, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  className="nav-dropdown-item"
                                  onClick={() => {
                                    if (venue === `Explore All Venues in ${selectedCity}` || venue === "Explore All Venues") {
                                      navigate(buildVenuesListingPath("all", {}));
                                    } else {
                                      const type = venue
                                        .toLowerCase()
                                        .replace(` in ${selectedCity.toLowerCase()}`, "")
                                        .replace(/s$/, "");

                                      navigate(
                                        buildVenuesListingPath(type.toLowerCase(), {
                                          ...(selectedCity ? { city: selectedCity } : {}),
                                        }),
                                      );
                                    }

                                    setDropdownState((prev) => ({
                                      ...prev,
                                      venues: { ...prev.venues, isHovered: false },
                                    }));
                                  }}
                                >
                                  {venue}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <>
                        {topRankedVenuesCount > 0 && (
                          <button
                            type="button"
                            className="nav-dropdown-item nav-dropdown-item-top-ranked"
                            onClick={() => {
                              navigate(
                                buildVenuesListingPath("all", {
                                  hasTopRank: true,
                                  ...(selectedCity ? { city: selectedCity } : {}),
                                }),
                              );
                              setDropdownState((prev) => ({
                                ...prev,
                                venues: { ...prev.venues, isHovered: false },
                              }));
                            }}
                          >
                            <span className="nav-dropdown-item-top-ranked-text">Top Venues</span>
                            <span className="nav-dropdown-item-top-ranked-star" aria-hidden="true">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                            </span>
                          </button>
                        )}
                        {venueTypes.map((venue, index) => (
                        <button
                          key={index}
                          className="nav-dropdown-item"
                          onClick={() => {
                            if (venue === `Explore All Venues in ${selectedCity}` || venue === "Explore All Venues") {
                              navigate(buildVenuesListingPath("all", {}));
                            } else {
                              const type = venue
                                .toLowerCase()
                                .replace(` in ${selectedCity.toLowerCase()}`, "")
                                .replace(/s$/, "");

                              navigate(
                                buildVenuesListingPath(type.toLowerCase(), {
                                  ...(selectedCity ? { city: selectedCity } : {}),
                                }),
                              );
                            }

                            setDropdownState((prev) => ({
                              ...prev,
                              venues: { ...prev.venues, isHovered: false },
                            }));
                          }}
                        >
                          {venue}
                        </button>
                      ))}
                      </>
                    )}
                  </div>,
                  document.body
                )}
            </div>
            <div
              className="nav-link-wrapper desktop-only"
              ref={vendorsMenuRef}
              onMouseEnter={() => handleDropdownHover("vendors", true)}
              onMouseLeave={() => handleDropdownHover("vendors", false)}
            >
              <div ref={vendorsLinkRef} className="nav-link">
                Vendors
              </div>
              {dropdownState.vendors.isHovered &&
                createPortal(
                  <div
                    className={`nav-dropdown-menu vendors-dropdown nav-dropdown-menu-portal ${vendorTypesTabular ? "nav-dropdown-menu--single-column" : ""}`}
                    style={{
                      position: "fixed",
                      top: `${dropdownState.vendors.position.top}px`,
                      left: `${dropdownState.vendors.position.left}px`,
                    }}
                    onMouseEnter={() => handleDropdownHover("vendors", true)}
                    onMouseLeave={() => handleDropdownHover("vendors", false)}
                  >
                    {vendorTypesTabular ? (
                      <table className="nav-dropdown-table" role="presentation">
                        <tbody>
                          {topRankedVendorsCount > 0 && (
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="nav-dropdown-item nav-dropdown-item-top-ranked"
                                  onClick={() => {
                                    navigate(
                                      buildVendorsListingPath({
                                        hasTopRank: true,
                                        ...(selectedCity ? { city: selectedCity } : {}),
                                      }),
                                    );
                                    setDropdownState((prev) => ({
                                      ...prev,
                                      vendors: { ...prev.vendors, isHovered: false },
                                    }));
                                  }}
                                >
                                  <span className="nav-dropdown-item-top-ranked-text">Top Vendors</span>
                                  <span className="nav-dropdown-item-top-ranked-star" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                  </span>
                                </button>
                              </td>
                            </tr>
                          )}
                          {vendorTypes.map((vendor, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  type="button"
                                  className="nav-dropdown-item"
                                  onClick={() => {
                                    if (vendor === `Explore All Vendors in ${selectedCity}` || vendor === "Explore All Vendors") {
                                      navigate(
                                        buildVendorsListingPath({
                                          category: "all",
                                          ...(selectedCity ? { city: selectedCity } : {}),
                                        }),
                                      );
                                    } else {
                                      const type = vendor
                                        .toLowerCase()
                                        .replace(` in ${selectedCity.toLowerCase()}`, "")
                                        .replace(/s$/, "");
                                      navigate(
                                        buildVendorsListingPath({
                                          category: type,
                                          ...(selectedCity ? { city: selectedCity } : {}),
                                        }),
                                      );
                                    }
                                    setDropdownState((prev) => ({
                                      ...prev,
                                      vendors: { ...prev.vendors, isHovered: false },
                                    }));
                                  }}
                                >
                                  {vendor}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <>
                        {topRankedVendorsCount > 0 && (
                          <button
                            type="button"
                            className="nav-dropdown-item nav-dropdown-item-top-ranked"
                            onClick={() => {
                              navigate(
                                buildVendorsListingPath({
                                  hasTopRank: true,
                                  ...(selectedCity ? { city: selectedCity } : {}),
                                }),
                              );
                              setDropdownState((prev) => ({
                                ...prev,
                                vendors: { ...prev.vendors, isHovered: false },
                              }));
                            }}
                          >
                            <span className="nav-dropdown-item-top-ranked-text">Top Vendors</span>
                            <span className="nav-dropdown-item-top-ranked-star" aria-hidden="true">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                            </span>
                          </button>
                        )}
                        {vendorTypes.map((vendor, index) => (
                        <button
                          key={index}
                          type="button"
                          className="nav-dropdown-item"
                          onClick={() => {
                            if (vendor === `Explore All Vendors in ${selectedCity}` || vendor === "Explore All Vendors") {
                              navigate(
                                buildVendorsListingPath({
                                  category: "all",
                                  ...(selectedCity ? { city: selectedCity } : {}),
                                }),
                              );
                            } else {
                              const type = vendor
                                .toLowerCase()
                                .replace(` in ${selectedCity.toLowerCase()}`, "")
                                .replace(/s$/, "");
                              navigate(
                                buildVendorsListingPath({
                                  category: type,
                                  ...(selectedCity ? { city: selectedCity } : {}),
                                }),
                              );
                            }
                            setDropdownState((prev) => ({
                              ...prev,
                              vendors: { ...prev.vendors, isHovered: false },
                            }));
                          }}
                        >
                          {vendor}
                        </button>
                      ))}
                      </>
                    )}
                  </div>,
                  document.body
                )}
            </div>
            {header.navItems
              .filter(
                (item) => item.label !== "Venues" && item.label !== "Vendors",
              )
              .map((item, index) => {
                if (item.path === "/blog") {
                  return (
                    <button
                      key={index}
                      className="nav-link desktop-only"
                      onClick={() => {
                        navigate(buildBlogPath(selectedCity));
                        setMobileState((prev) => ({
                          ...prev,
                          menuOpen: false,
                        }));
                      }}
                    >
                      {item.label}
                    </button>
                  );
                }
                return (
                  <button
                    key={index}
                    type="button"
                    className="nav-link desktop-only"
                    onClick={() => {
                      navigate(item.path);
                      setMobileState((prev) => ({ ...prev, menuOpen: false }));
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}

            {/* Mobile Menu Items */}
            <div className="mobile-menu-content">
              <button
                className="mobile-nav-link"
                onClick={() =>
                  setMobileState((prev) => ({
                    ...prev,
                    venuesOpen: !prev.venuesOpen,
                  }))
                }
              >
                Venues
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={mobileState.venuesOpen ? "rotated" : ""}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {mobileState.venuesOpen && (
                <div className="mobile-dropdown">
                  {topRankedVenuesCount > 0 && (
                    <button
                      type="button"
                      className="mobile-dropdown-item mobile-dropdown-item-top-ranked"
                      onClick={() => {
                        navigate(
                          buildVenuesListingPath("all", {
                            hasTopRank: true,
                            ...(selectedCity ? { city: selectedCity } : {}),
                          }),
                        );
                        setMobileState({
                          menuOpen: false,
                          venuesOpen: false,
                          vendorsOpen: false,
                        });
                      }}
                    >
                      Top Venues
                    </button>
                  )}
                  {venueTypes.map((venue, index) => (
                    <button
                      key={index}
                      type="button"
                      className="mobile-dropdown-item"
                      onClick={() => {
                        navigateVenueFromLabel(venue);
                        closeMobileMenu();
                      }}
                    >
                      {venue}
                    </button>
                  ))}
                </div>
              )}

              <button
                className="mobile-nav-link"
                onClick={() =>
                  setMobileState((prev) => ({
                    ...prev,
                    vendorsOpen: !prev.vendorsOpen,
                  }))
                }
              >
                Vendors
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={mobileState.vendorsOpen ? "rotated" : ""}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {mobileState.vendorsOpen && (
                <div className="mobile-dropdown">
                  {topRankedVendorsCount > 0 && (
                    <button
                      type="button"
                      className="mobile-dropdown-item mobile-dropdown-item-top-ranked"
                      onClick={() => {
                        navigate(
                          buildVendorsListingPath({
                            hasTopRank: true,
                            ...(selectedCity ? { city: selectedCity } : {}),
                          }),
                        );
                        setMobileState({
                          menuOpen: false,
                          venuesOpen: false,
                          vendorsOpen: false,
                        });
                      }}
                    >
                      Top Vendors
                    </button>
                  )}
                  {vendorTypes.map((vendor, index) => (
                    <button
                      key={index}
                      type="button"
                      className="mobile-dropdown-item"
                      onClick={() => {
                        navigateVendorFromLabel(vendor);
                        closeMobileMenu();
                      }}
                    >
                      {vendor}
                    </button>
                  ))}
                </div>
              )}

              {header.navItems
                .filter(
                  (item) => item.label !== "Venues" && item.label !== "Vendors",
                )
                .map((item, index) => {
                  if (item.path === "/blog") {
                    return (
                      <button
                        key={index}
                        className="mobile-nav-link"
                        onClick={() => {
                          navigate(buildBlogPath(selectedCity));
                          setMobileState((prev) => ({
                            ...prev,
                            menuOpen: false,
                          }));
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  }
                  return (
                    <button
                      key={index}
                      type="button"
                      className="mobile-nav-link"
                      onClick={() => {
                        navigate(item.path);
                        setMobileState((prev) => ({ ...prev, menuOpen: false }));
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}

              {!isLoggedIn && !isAdminLoggedIn && (
                <button
                  className="mobile-nav-link mobile-cta"
                  onClick={() => {
                    if (onListBusinessClick) onListBusinessClick();
                    else navigate("/register?type=business");
                    setMobileState((prev) => ({ ...prev, menuOpen: false }));
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  {header.listBusinessText}
                </button>
              )}

              {!isLoggedIn && !isAdminLoggedIn && !basic && (
                <button
                  className="mobile-nav-link mobile-login-cta"
                  onClick={() => {
                    setMobileState({
                      menuOpen: false,
                      venuesOpen: false,
                      vendorsOpen: false,
                    });
                    if (onProfileClick) onProfileClick();
                    else navigate("/login");
                  }}
                >
                  Login
                </button>
              )}

              {!isBusiness && !isAdminLoggedIn && (isHeaderCollapsed || isLoggedIn) && (
                <button
                  className="mobile-nav-link mobile-cta"
                  onClick={() => {
                    setMobileState((prev) => ({ ...prev, menuOpen: false }));
                    if (onEnquireClick) onEnquireClick();
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  {header.callButtonText}
                </button>
              )}

            </div>
          </>
        )}
      </nav>
    </header>
  );
}

export default Header;
